import {
  AnyFn,
  Async,
  AsyncError,
  AsyncFetched,
  AsyncFetching,
  AsyncNotFetched,
  F1,
  SMap,
  TMap,
} from './types';

const fetchedKey: AsyncFetched['type'] = 'fetched';
const errorKey: AsyncError['type'] = 'fetch-error';
type NotFunction<T> = Exclude<T, AnyFn>;

export type LightAsyncMapper<TOut = string> = Partial<
  TMap<Async['type'], TOut>
>;
export type AsyncMapper<
  TValue = unknown,
  TErr = string,
  TOut extends NotFunction<unknown> = string,
> =
  | LightAsyncMapper<TOut>
  | (Partial<Pick<TMap<Async['type'], TOut>, 'fetching' | 'not-fetched'>> & {
      [fetchedKey]?: F1<TValue, TOut> | TOut;
      [errorKey]?: F1<TErr, TOut> | TOut;
    });

export const mapAsyncValue = <TValue, TErr, TOut extends NotFunction<unknown>>(
  state: Async<TValue, TErr>,
  mapper: LightAsyncMapper<TOut> | AsyncMapper<TValue, TErr, TOut>,
): TOut | null => {
  switch (state.type) {
    case 'fetch-error':
    case 'fetched': {
      const v = mapper[state.type];
      if (!v) return null;
      if (typeof v !== 'function') return v;
      return (v as F1<TValue | TErr, TOut>)(state.value);
    }
    case 'fetching':
    case 'not-fetched':
      return mapper[state.type] ?? null;
  }
};

export const isAllFinished = <T>(map: SMap<Async<T>>): boolean => {
  const values = Object.values(map);
  return (
    values.length === 0 ||
    values.every((v) => v.type === 'fetched' || v.type === 'fetch-error')
  );
};

export const flattenAsyncMap = <T>(map: SMap<Async<T>>): Async<T> => {
  const values = Object.values(map);
  if (values.length === 0 || values.every((v) => v.type === 'not-fetched'))
    return makeAsync.notFetched();
  if (values.some((v) => v.type === 'fetching')) return makeAsync.fetching();
  const err = values.find((v) => v.type === 'fetch-error');
  if (err) return err;
  return makeAsync.error('foo');
};

export const makeAsync = {
  notFetched: (): AsyncNotFetched => ({
    type: 'not-fetched',
  }),
  fetching: (): AsyncFetching => ({
    type: 'fetching',
  }),
  fetched: <T>(value: T): AsyncFetched<T> => ({
    type: 'fetched',
    value,
  }),
  error: <TErr>(value: TErr): AsyncError<TErr> => ({
    type: 'fetch-error',
    value,
  }),
};
